import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import axios from 'axios'

// ── Constants ──────────────────────────────────────────────────────────────────

const COLOR_FAMILIES = ['BROWN', 'GOLD', 'RED', 'GRAY', 'WHITE', 'BLACK', 'TAN', 'PINK', 'GREEN', 'MIXED', 'UNKNOWN']
const CATEGORIES = ['DECORATIVE_ROCK', 'CONSTRUCTION_AGGREGATE', 'RIP_RAP', 'SAND', 'BOULDER', 'FLAGSTONE', 'SOIL_OR_PLANTING_MIX', 'UNKNOWN']

// Color swatch backgrounds per color family
const COLOR_SWATCH = {
  BROWN: '#8B4513',
  GOLD: '#DAA520',
  RED: '#C62828',
  GRAY: '#757575',
  WHITE: '#e0e0e0',
  BLACK: '#212121',
  TAN: '#D2B48C',
  PINK: '#E91E63',
  GREEN: '#2E7D32',
  MIXED: '#9C27B0',
  UNKNOWN: '#bdbdbd'
}

// Color chip text contrast
const COLOR_CHIP_TEXT = {
  BROWN: '#fff', GOLD: '#333', RED: '#fff', GRAY: '#fff', WHITE: '#333',
  BLACK: '#fff', TAN: '#333', PINK: '#fff', GREEN: '#fff', MIXED: '#fff', UNKNOWN: '#333'
}

// Pricing mode badge styles
const PRICING_MODE_STYLE = {
  PIT_PICKUP:  { bg: '#e8f5e9', color: '#2e7d32' },
  YARD_PICKUP: { bg: '#e3f2fd', color: '#1565c0' },
  DELIVERED:   { bg: '#fff3e0', color: '#e65100' },
  RETAIL:      { bg: '#f3e5f5', color: '#7b1fa2' },
  WHOLESALE:   { bg: '#e0f2f1', color: '#00695c' }
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  tabHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  // Table
  headerCell: {
    padding: theme.spacing(0.75, 1.5),
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  cellCompact: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.82rem'
  },
  swatch: {
    width: 36,
    height: 36,
    borderRadius: 4,
    border: '1px solid rgba(0,0,0,0.12)',
    flexShrink: 0
  },
  thumbnail: {
    width: 36,
    height: 36,
    objectFit: 'cover',
    borderRadius: 4,
    border: '1px solid #eee',
    display: 'block'
  },
  expandArrow: {
    color: '#757575',
    display: 'block'
  },
  expandedHeaderRow: {
    backgroundColor: '#f5f5f5'
  },
  // Detail panel
  detailPanel: {
    padding: theme.spacing(2, 3, 3, 3),
    borderTop: `2px solid ${theme.palette.primary.light}`,
    backgroundColor: '#fafafa'
  },
  sectionToggleBar: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1)
  },
  sectionToggleBtn: {
    fontSize: '0.8rem',
    padding: theme.spacing(0.5, 1.5),
    textTransform: 'none',
    borderRadius: 3
  },
  // Suppliers & Pricing section
  supplierTable: {
    fontSize: '0.8rem'
  },
  pricingSummaryBar: {
    display: 'flex',
    gap: theme.spacing(3),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  priceSummaryItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  priceSummaryLabel: {
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary
  },
  priceSummaryValue: {
    fontWeight: 700,
    fontSize: '1rem'
  },
  bestPitPrice: {
    color: '#2e7d32'
  },
  // Gradations section
  gradationAccordion: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
    backgroundColor: '#fff',
    overflow: 'hidden'
  },
  gradationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.5),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  gradationBody: {
    padding: theme.spacing(1, 2, 1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fafafa'
  },
  // Notes & Admin section
  adminForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    maxWidth: 480
  },
  adminActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap'
  },
  saveBtn: {
    fontSize: '0.8rem'
  },
  emptyState: {
    padding: theme.spacing(5),
    textAlign: 'center'
  },
  detailLoadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120
  }
}))

// ── Add Material Dialog ────────────────────────────────────────────────────────

function AddMaterialDialog ({ open, onClose, onCreated }) {
  const [displayName, setDisplayName] = useState('')
  const [colorFamily, setColorFamily] = useState('UNKNOWN')
  const [materialCategory, setMaterialCategory] = useState('UNKNOWN')
  const [dupMatches, setDupMatches] = useState([])
  const [dupLoading, setDupLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')
  const [searchTimer, setSearchTimer] = useState(null)

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setDisplayName('')
      setColorFamily('UNKNOWN')
      setMaterialCategory('UNKNOWN')
      setDupMatches([])
      setDupLoading(false)
      setCreating(false)
      setCreateError('')
      if (searchTimer) clearTimeout(searchTimer)
      setSearchTimer(null)
    }

  }, [open])

  const handleNameChange = (e) => {
    const val = e.target.value
    setDisplayName(val)
    setCreateError('')

    if (searchTimer) clearTimeout(searchTimer)

    if (val.trim().length < 2) {
      setDupMatches([])
      setDupLoading(false)
      setSearchTimer(null)
      return
    }

    setDupLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get('/api/aggregate-dashboard/materials', { params: { search: val.trim() } })
        const matches = Array.isArray(res.data) ? res.data : []
        setDupMatches(matches.slice(0, 5))
      } catch (err) {
        setDupMatches([])
      } finally {
        setDupLoading(false)
      }
    }, 300)
    setSearchTimer(timer)
  }

  const handleUseExisting = (material) => {
    onClose()
    if (onCreated) onCreated(material._id, false)
  }

  const handleCreate = async () => {
    if (!displayName.trim()) {
      setCreateError('Display name is required.')
      return
    }
    setCreating(true)
    setCreateError('')
    try {
      const res = await axios.post('/api/aggregate-dashboard/materials', {
        displayName: displayName.trim(),
        colorFamily,
        materialCategory
      })
      const newMaterial = res.data
      onClose()
      if (onCreated) onCreated(newMaterial._id || newMaterial.id, true)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setCreateError(msg)
    } finally {
      setCreating(false)
    }
  }

  const canCreate = displayName.trim().length >= 3

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Material Profile</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 4 }}>
          {/* Display Name with live duplicate search */}
          <TextField
            label="Display Name"
            value={displayName}
            onChange={handleNameChange}
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            placeholder="e.g. Desert Gold Decomposed Granite"
            helperText="Enter at least 3 characters to check for duplicates."
          />

          {/* Duplicate matches */}
          {dupLoading && (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary">Searching for duplicates...</Typography>
            </Box>
          )}

          {!dupLoading && dupMatches.length > 0 && (
            <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fffde7' }}>
              <Box style={{ padding: '8px 12px 4px 12px' }}>
                <Typography variant="caption" style={{ fontWeight: 700, color: '#e65100' }}>
                  Did you mean one of these?
                </Typography>
              </Box>
              <List dense disablePadding>
                {dupMatches.map((m) => {
                  const swatchColor = COLOR_SWATCH[m.colorFamily] || COLOR_SWATCH.UNKNOWN
                  const chipTextColor = COLOR_CHIP_TEXT[m.colorFamily] || '#333'
                  return (
                    <ListItem
                      key={m._id}
                      style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 3,
                          backgroundColor: swatchColor,
                          border: '1px solid rgba(0,0,0,0.12)',
                          flexShrink: 0
                        }}
                      />
                      <ListItemText
                        primary={m.displayName}
                        secondary={(m.materialCategory || '').replace(/_/g, ' ')}
                        primaryTypographyProps={{ variant: 'body2', style: { fontWeight: 600 } }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                        style={{ flex: 1, margin: 0 }}
                      />
                      <Chip
                        label={m.colorFamily || 'UNKNOWN'}
                        size="small"
                        style={{
                          backgroundColor: swatchColor,
                          color: chipTextColor,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 18,
                          border: m.colorFamily === 'WHITE' ? '1px solid #bdbdbd' : undefined
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', marginLeft: 4 }}
                        onClick={() => handleUseExisting(m)}
                      >
                        Use this instead
                      </Button>
                    </ListItem>
                  )
                })}
              </List>
            </Box>
          )}

          {/* Color Family */}
          <TextField
            select
            label="Color Family"
            value={colorFamily}
            onChange={(e) => setColorFamily(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          >
            {COLOR_FAMILIES.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>

          {/* Material Category */}
          <TextField
            select
            label="Material Category"
            value={materialCategory}
            onChange={(e) => setMaterialCategory(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>
            ))}
          </TextField>

          {createError && (
            <Typography variant="caption" style={{ color: '#c62828' }}>{createError}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '12px 20px' }}>
        <Button onClick={onClose} size="small" disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleCreate}
          disabled={!canCreate || creating}
          startIcon={creating ? <CircularProgress size={14} /> : <AddIcon />}
        >
          {creating ? 'Creating...' : 'Create New Material'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Add Supplier / Offering Dialog ────────────────────────────────────────────

const PRICING_MODES = ['RETAIL', 'WHOLESALE', 'PIT_PICKUP', 'YARD_PICKUP', 'DELIVERED']
const AVAILABILITY_STATUSES = ['IN_STOCK', 'OUT_OF_STOCK', 'UNKNOWN']
const UNIT_OPTIONS = ['TON', 'EACH', 'LB', 'SQFT', 'CY', 'UNKNOWN']

function AddSupplierOfferingDialog ({ open, onClose, materialProfileId, existingSuppliers, onCreated }) {
  // Form fields
  const [vendorId, setVendorId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [gradationId, setGradationId] = useState('')
  const [pricingMode, setPricingMode] = useState('RETAIL')
  const [price, setPrice] = useState('')
  const [unit, setUnit] = useState('TON')
  const [productTitle, setProductTitle] = useState('')
  const [vendorSku, setVendorSku] = useState('')
  const [sourceProductUrl, setSourceProductUrl] = useState('')
  const [availabilityStatus, setAvailabilityStatus] = useState('UNKNOWN')

  // Fetched data
  const [vendors, setVendors] = useState([])
  const [locations, setLocations] = useState([])
  const [gradations, setGradations] = useState([])
  const [loadingLocations, setLoadingLocations] = useState(false)

  // Submit state
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setVendorId('')
      setLocationId('')
      setGradationId('')
      setPricingMode('RETAIL')
      setPrice('')
      setUnit('TON')
      setProductTitle('')
      setVendorSku('')
      setSourceProductUrl('')
      setAvailabilityStatus('UNKNOWN')
      setLocations([])
      setSaveError('')

      // Populate vendor list from the existing suppliers detail data
      if (existingSuppliers && existingSuppliers.length > 0) {
        const seen = new Set()
        const uniqueVendors = []
        for (const s of existingSuppliers) {
          if (s.vendor && s.vendor._id && !seen.has(String(s.vendor._id))) {
            seen.add(String(s.vendor._id))
            uniqueVendors.push(s.vendor)
          }
        }
        setVendors(uniqueVendors)
      } else {
        // Fall back to fetching from locations endpoint
        axios.get('/api/aggregate-dashboard/locations', { params: { limit: 50 } })
          .then((res) => {
            const locs = Array.isArray(res.data) ? res.data : []
            const seen = new Set()
            const uniqueVendors = []
            for (const loc of locs) {
              const v = loc.vendorId
              if (v && v._id && !seen.has(String(v._id))) {
                seen.add(String(v._id))
                uniqueVendors.push(v)
              }
            }
            setVendors(uniqueVendors)
          })
          .catch(() => setVendors([]))
      }

      // Fetch gradations
      axios.get('/api/aggregate-dashboard/gradations')
        .then((res) => setGradations(Array.isArray(res.data) ? res.data : []))
        .catch(() => setGradations([]))
    }

  }, [open])

  // When vendor changes, load its locations
  useEffect(() => {
    if (!vendorId) {
      setLocations([])
      setLocationId('')
      return
    }
    setLoadingLocations(true)
    setLocationId('')
    axios.get('/api/aggregate-dashboard/locations', { params: { vendorId, limit: 50 } })
      .then((res) => setLocations(Array.isArray(res.data) ? res.data : []))
      .catch(() => setLocations([]))
      .finally(() => setLoadingLocations(false))
  }, [vendorId])

  // When gradation changes, auto-set unit from gradation's defaultUnit
  const handleGradationChange = (e) => {
    const gId = e.target.value
    setGradationId(gId)
    const grad = gradations.find((g) => String(g._id) === gId)
    if (grad && grad.defaultUnit) {
      setUnit(grad.defaultUnit)
    }
  }

  const handleSave = async () => {
    setSaveError('')
    if (!vendorId) { setSaveError('Vendor is required.'); return }
    if (!productTitle.trim()) { setSaveError('Product title is required.'); return }
    if (price !== '' && isNaN(Number(price))) { setSaveError('Price must be a number.'); return }

    setSaving(true)
    try {
      const body = {
        vendorId,
        materialProfileId,
        productTitle: productTitle.trim(),
        pricingMode,
        unit,
        availabilityStatus,
        sourceType: 'MANUAL_ENTRY'
      }
      if (locationId) body.supplierLocationId = locationId
      if (gradationId) body.gradationId = gradationId
      if (price !== '') body.price = Number(price)
      if (vendorSku.trim()) body.vendorSku = vendorSku.trim()
      if (sourceProductUrl.trim()) body.sourceProductUrl = sourceProductUrl.trim()

      await axios.post('/api/aggregate-dashboard/offerings', body)
      onClose()
      if (onCreated) onCreated()
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setSaveError(msg)
    } finally {
      setSaving(false)
    }
  }

  const canSave = vendorId && productTitle.trim().length > 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Supplier Offering</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 4 }}>

          {/* Vendor */}
          <TextField
            select
            label="Vendor *"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            helperText={vendors.length === 0 ? 'No vendors loaded — sync a vendor first.' : ''}
          >
            {vendors.map((v) => (
              <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
            ))}
          </TextField>

          {/* Supplier Location */}
          <TextField
            select
            label="Supplier Location"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            disabled={!vendorId || loadingLocations}
            helperText={!vendorId ? 'Select a vendor first.' : loadingLocations ? 'Loading locations...' : ''}
          >
            <MenuItem value=""><em>None / Unknown</em></MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc._id} value={loc._id}>
                {loc.name} {loc.locationType ? `(${loc.locationType.replace(/_/g, ' ')})` : ''}
              </MenuItem>
            ))}
          </TextField>

          {/* Gradation */}
          <TextField
            select
            label="Gradation"
            value={gradationId}
            onChange={handleGradationChange}
            variant="outlined"
            size="small"
            fullWidth
          >
            <MenuItem value=""><em>None / Unknown</em></MenuItem>
            {gradations.map((g) => (
              <MenuItem key={g._id} value={g._id}>
                {g.displayName} {g.aggregateType ? `· ${g.aggregateType}` : ''}
              </MenuItem>
            ))}
          </TextField>

          {/* Product Title */}
          <TextField
            label="Product Title *"
            value={productTitle}
            onChange={(e) => setProductTitle(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="e.g. Desert Gold 3/8 Decomposed Granite"
            helperText="The vendor's name for this product."
          />

          {/* Pricing Mode + Price + Unit in a row */}
          <Box display="flex" style={{ gap: 12 }}>
            <TextField
              select
              label="Pricing Mode *"
              value={pricingMode}
              onChange={(e) => setPricingMode(e.target.value)}
              variant="outlined"
              size="small"
              style={{ flex: 2 }}
            >
              {PRICING_MODES.map((m) => (
                <MenuItem key={m} value={m}>{m.replace(/_/g, ' ')}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              variant="outlined"
              size="small"
              style={{ flex: 1.5 }}
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              placeholder="e.g. 42.50"
            />

            <TextField
              select
              label="Unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              variant="outlined"
              size="small"
              style={{ flex: 1 }}
            >
              {UNIT_OPTIONS.map((u) => (
                <MenuItem key={u} value={u}>{u}</MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Vendor SKU */}
          <TextField
            label="Vendor SKU"
            value={vendorSku}
            onChange={(e) => setVendorSku(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="e.g. DG-38-BLK"
          />

          {/* Source URL */}
          <TextField
            label="Source URL"
            value={sourceProductUrl}
            onChange={(e) => setSourceProductUrl(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="https://vendor.com/product/..."
          />

          {/* Availability Status */}
          <TextField
            select
            label="Availability Status"
            value={availabilityStatus}
            onChange={(e) => setAvailabilityStatus(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          >
            {AVAILABILITY_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
            ))}
          </TextField>

          {saveError && (
            <Typography variant="caption" style={{ color: '#c62828' }}>{saveError}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '12px 20px' }}>
        <Button onClick={onClose} size="small" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={!canSave || saving}
          startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}
        >
          {saving ? 'Saving...' : 'Add Offering'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Pricing mode badge ─────────────────────────────────────────────────────────

function PricingModeBadge ({ mode }) {
  const style = PRICING_MODE_STYLE[mode] || { bg: '#f5f5f5', color: '#757575' }
  return (
    <Chip
      label={mode}
      size="small"
      style={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.65rem',
        height: 18,
        marginRight: 3,
        marginBottom: 2
      }}
    />
  )
}

// ── Format price ───────────────────────────────────────────────────────────────

function fmt (val) {
  if (val == null) return '—'
  return '$' + Number(val).toFixed(2)
}

// ── Suppliers & Pricing inner section ─────────────────────────────────────────

function SuppliersSection ({ detail }) {
  const classes = useStyles()
  const { suppliers = [], offeringCount = 0 } = detail

  // Compute overall best prices across all suppliers
  let bestPitPrice = null
  let retailYardPrice = null
  for (const s of suppliers) {
    if (s.bestPitPrice != null && (bestPitPrice == null || s.bestPitPrice < bestPitPrice)) bestPitPrice = s.bestPitPrice
    if (s.bestRetailPrice != null && (retailYardPrice == null || s.bestRetailPrice < retailYardPrice)) retailYardPrice = s.bestRetailPrice
  }

  if (suppliers.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ padding: '16px 0' }}>
        No supplier offerings found for this material.
      </Typography>
    )
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 0 }}>
        <Table size="small" className={classes.supplierTable}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Supplier</TableCell>
              <TableCell className={classes.headerCell}>Source Types</TableCell>
              <TableCell className={classes.headerCell}>Pit Price</TableCell>
              <TableCell className={classes.headerCell}>Yard / Retail Price</TableCell>
              <TableCell className={classes.headerCell}>Last Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {suppliers.map((s, idx) => {
              const name = s.vendor ? s.vendor.name : 'Unknown Vendor'
              const lastUpdated = s.lastUpdated ? new Date(s.lastUpdated).toLocaleDateString() : '—'
              return (
                <TableRow key={idx} hover>
                  <TableCell className={classes.cellCompact}>
                    <strong>{name}</strong>
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    <Box display="flex" flexWrap="wrap">
                      {(s.sourceTypes || []).map((m) => <PricingModeBadge key={m} mode={m} />)}
                    </Box>
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    {fmt(s.bestPitPrice)}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    {fmt(s.bestRetailPrice)}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    <Typography variant="caption" color="textSecondary">{lastUpdated}</Typography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pricing summary bar */}
      <div className={classes.pricingSummaryBar}>
        <div className={classes.priceSummaryItem}>
          <span className={classes.priceSummaryLabel}>Best Pit Price</span>
          <span className={`${classes.priceSummaryValue} ${classes.bestPitPrice}`}>
            {fmt(bestPitPrice)}
          </span>
        </div>
        <div className={classes.priceSummaryItem}>
          <span className={classes.priceSummaryLabel}>Retail / Yard Price</span>
          <span className={classes.priceSummaryValue}>
            {fmt(retailYardPrice)}
          </span>
        </div>
        <div className={classes.priceSummaryItem}>
          <span className={classes.priceSummaryLabel}>Offering Count</span>
          <span className={classes.priceSummaryValue}>{offeringCount}</span>
        </div>
      </div>
    </>
  )
}

// ── Gradations inner section ───────────────────────────────────────────────────

function GradationsSection ({ detail }) {
  const classes = useStyles()
  const { gradations = [] } = detail
  const [openGradId, setOpenGradId] = useState(null)

  if (gradations.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ padding: '16px 0' }}>
        No gradation data available for this material.
      </Typography>
    )
  }

  return (
    <div>
      {gradations.map((g, idx) => {
        const grad = g.gradation
        const gradId = grad ? grad._id : `nograd-${idx}`
        const gradName = grad ? grad.displayName : 'Unknown Gradation'
        const gradType = grad ? grad.aggregateType : null
        const isOpen = openGradId === gradId
        const offerings = g.offerings || []

        return (
          <div key={gradId} className={classes.gradationAccordion}>
            <div
              className={classes.gradationHeader}
              onClick={() => setOpenGradId((prev) => (prev === gradId ? null : gradId))}
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>{gradName}</Typography>
                {gradType && (
                  <Chip
                    label={gradType}
                    size="small"
                    style={{
                      fontSize: '0.65rem',
                      height: 18,
                      fontWeight: 600,
                      backgroundColor: '#e3f2fd',
                      color: '#1565c0'
                    }}
                  />
                )}
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography variant="caption" color="textSecondary">
                  {offerings.length} offering{offerings.length !== 1 ? 's' : ''}
                </Typography>
                {isOpen
                  ? <ExpandLessIcon fontSize="small" style={{ color: '#757575' }} />
                  : <ExpandMoreIcon fontSize="small" style={{ color: '#757575' }} />}
              </Box>
            </div>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <div className={classes.gradationBody}>
                {offerings.length === 0 ? (
                  <Typography variant="caption" color="textSecondary">No offerings.</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headerCell}>Supplier</TableCell>
                        <TableCell className={classes.headerCell}>Mode</TableCell>
                        <TableCell className={classes.headerCell}>Price</TableCell>
                        <TableCell className={classes.headerCell}>Unit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {offerings.map((off, oi) => (
                        <TableRow key={oi}>
                          <TableCell className={classes.cellCompact}>
                            {off.vendorId ? off.vendorId.name : '—'}
                          </TableCell>
                          <TableCell className={classes.cellCompact}>
                            <PricingModeBadge mode={off.pricingMode} />
                          </TableCell>
                          <TableCell className={classes.cellCompact}>
                            {fmt(off.price)}
                          </TableCell>
                          <TableCell className={classes.cellCompact}>
                            {off.unit || grad && grad.defaultUnit || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </Collapse>
          </div>
        )
      })}
    </div>
  )
}

// ── Merge Material Dialog ──────────────────────────────────────────────────────

function MergeMaterialDialog ({ open, onClose, sourceMaterial, onMerged }) {
  const [search, setSearch] = useState('')
  const [searchTimer, setSearchTimer] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [selectedTarget, setSelectedTarget] = useState(null)
  const [merging, setMerging] = useState(false)
  const [mergeError, setMergeError] = useState('')

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setSearch('')
      setSearchResults([])
      setSelectedTarget(null)
      setMerging(false)
      setMergeError('')
      if (searchTimer) clearTimeout(searchTimer)
      setSearchTimer(null)
    }

  }, [open])

  const handleSearchChange = (e) => {
    const val = e.target.value
    setSearch(val)
    setSelectedTarget(null)
    setMergeError('')

    if (searchTimer) clearTimeout(searchTimer)

    if (val.trim().length < 2) {
      setSearchResults([])
      setSearchLoading(false)
      setSearchTimer(null)
      return
    }

    setSearchLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get('/api/aggregate-dashboard/materials', { params: { search: val.trim() } })
        const all = Array.isArray(res.data) ? res.data : []
        // Exclude the source material itself
        setSearchResults(all.filter((m) => String(m._id) !== String(sourceMaterial._id)).slice(0, 8))
      } catch (err) {
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 300)
    setSearchTimer(timer)
  }

  const handleConfirmMerge = async () => {
    if (!selectedTarget) return
    setMerging(true)
    setMergeError('')
    try {
      await axios.post('/api/aggregate-dashboard/materials/merge', {
        sourceId: sourceMaterial._id,
        targetId: selectedTarget._id
      })
      onClose()
      if (onMerged) onMerged()
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setMergeError(msg)
    } finally {
      setMerging(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Merge Material</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 4 }}>
          <Typography variant="body2" color="textSecondary">
            All offerings from <strong>{sourceMaterial ? sourceMaterial.displayName : ''}</strong> will be moved to the target material. The source will be archived.
          </Typography>

          <TextField
            label="Search target material"
            value={search}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            fullWidth
            autoFocus
            placeholder="Type at least 2 characters..."
            helperText="Search for the material to merge into."
          />

          {searchLoading && (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary">Searching...</Typography>
            </Box>
          )}

          {!searchLoading && searchResults.length > 0 && !selectedTarget && (
            <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
              <List dense disablePadding>
                {searchResults.map((m) => {
                  const swatchColor = COLOR_SWATCH[m.colorFamily] || COLOR_SWATCH.UNKNOWN
                  const chipTextColor = COLOR_CHIP_TEXT[m.colorFamily] || '#333'
                  return (
                    <ListItem
                      key={m._id}
                      button
                      onClick={() => setSelectedTarget(m)}
                      style={{ padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: 3,
                        backgroundColor: swatchColor,
                        border: '1px solid rgba(0,0,0,0.12)',
                        flexShrink: 0
                      }} />
                      <ListItemText
                        primary={m.displayName}
                        secondary={(m.materialCategory || '').replace(/_/g, ' ')}
                        primaryTypographyProps={{ variant: 'body2', style: { fontWeight: 600 } }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                        style={{ flex: 1, margin: 0 }}
                      />
                      <Chip
                        label={m.colorFamily || 'UNKNOWN'}
                        size="small"
                        style={{
                          backgroundColor: swatchColor,
                          color: chipTextColor,
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 18,
                          border: m.colorFamily === 'WHITE' ? '1px solid #bdbdbd' : undefined
                        }}
                      />
                    </ListItem>
                  )
                })}
              </List>
            </Box>
          )}

          {selectedTarget && (
            <Box style={{ padding: '10px 14px', backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4 }}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                Confirm Merge
              </Typography>
              <Typography variant="body2">
                Merge <strong>{sourceMaterial ? sourceMaterial.displayName : ''}</strong> into <strong>{selectedTarget.displayName}</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
                All offerings from the source will be moved to the target. Conflicting offerings keep the newest. The source material will be archived and cannot be undone.
              </Typography>
              <Button
                size="small"
                style={{ marginTop: 8, fontSize: '0.72rem' }}
                onClick={() => setSelectedTarget(null)}
              >
                Choose different target
              </Button>
            </Box>
          )}

          {mergeError && (
            <Typography variant="caption" style={{ color: '#c62828' }}>{mergeError}</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '12px 20px' }}>
        <Button onClick={onClose} size="small" disabled={merging}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={handleConfirmMerge}
          disabled={!selectedTarget || merging}
          startIcon={merging ? <CircularProgress size={14} /> : null}
        >
          {merging ? 'Merging...' : 'Confirm Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Notes & Admin inner section ────────────────────────────────────────────────

function NotesAdminSection ({ material, onSaved, onOpenAddMaterial, onOpenAddSupplier, onMerged }) {
  const classes = useStyles()
  const [displayName, setDisplayName] = useState(material.displayName || '')
  const [colorFamily, setColorFamily] = useState(material.colorFamily || 'UNKNOWN')
  const [materialCategory, setMaterialCategory] = useState(material.materialCategory || 'UNKNOWN')
  const [notes, setNotes] = useState(material.notes || '')
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')
  const [saveError, setSaveError] = useState('')
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)

  const handleSave = async () => {
    if (!displayName.trim()) { setSaveError('Display name is required'); return }
    setSaving(true)
    setSaveMsg('')
    setSaveError('')
    try {
      await axios.patch(`/api/aggregate-dashboard/materials/${material._id}`, {
        displayName: displayName.trim(),
        colorFamily,
        materialCategory,
        notes
      })
      setSaveMsg('Saved.')
      if (onSaved) onSaved({ displayName: displayName.trim(), colorFamily, materialCategory, notes })
      setTimeout(() => setSaveMsg(''), 2500)
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.error
        ? err.response.data.error
        : err.message
      setSaveError(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className={classes.adminForm}>
        <TextField
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
        />
        <TextField
          select
          label="Color Family"
          value={colorFamily}
          onChange={(e) => setColorFamily(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
        >
          {COLOR_FAMILIES.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Material Category"
          value={materialCategory}
          onChange={(e) => setMaterialCategory(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={3}
          placeholder="Internal notes about this material profile..."
        />

        {saveError && (
          <Typography variant="caption" style={{ color: '#c62828' }}>{saveError}</Typography>
        )}
        {saveMsg && (
          <Typography variant="caption" style={{ color: '#2e7d32' }}>{saveMsg}</Typography>
        )}
      </div>

      <div className={classes.adminActions}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          className={classes.saveBtn}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={16} /> : 'Save Changes'}
        </Button>

        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onOpenAddMaterial}>
          + Add Material
        </Button>

        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onOpenAddSupplier}>
          + Add Supplier
        </Button>

        <Button variant="outlined" size="small" color="secondary" onClick={() => setMergeDialogOpen(true)}>
          Merge with...
        </Button>
      </div>

      <MergeMaterialDialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        sourceMaterial={material}
        onMerged={() => {
          setMergeDialogOpen(false)
          if (onMerged) onMerged()
        }}
      />
    </div>
  )
}

// ── Detail panel ───────────────────────────────────────────────────────────────

const DETAIL_SECTIONS = ['Suppliers & Pricing', 'Gradations', 'Notes & Admin']

function DetailPanel ({ materialId, onMaterialPatched, onOpenAddMaterial, onMaterialMerged }) {
  const classes = useStyles()
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeSection, setActiveSection] = useState('Suppliers & Pricing')
  const [addSupplierOpen, setAddSupplierOpen] = useState(false)

  const fetchDetail = useCallback(() => {
    setLoading(true)
    setError('')
    axios.get(`/api/aggregate-dashboard/materials/${materialId}/detail`)
      .then((res) => setDetail(res.data))
      .catch((err) => {
        const msg = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : err.message
        setError(msg)
      })
      .finally(() => setLoading(false))
  }, [materialId])

  useEffect(() => {
    setDetail(null)
    fetchDetail()
  }, [fetchDetail])

  if (loading) {
    return (
      <div className={classes.detailPanel}>
        <div className={classes.detailLoadingBox}>
          <CircularProgress size={28} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={classes.detailPanel}>
        <Typography variant="body2" color="error">Error loading detail: {error}</Typography>
      </div>
    )
  }

  if (!detail) return null

  return (
    <div className={classes.detailPanel}>
      {/* Section toggle buttons */}
      <div className={classes.sectionToggleBar}>
        {DETAIL_SECTIONS.map((s) => (
          <Button
            key={s}
            className={classes.sectionToggleBtn}
            variant={activeSection === s ? 'contained' : 'text'}
            color={activeSection === s ? 'primary' : 'default'}
            size="small"
            onClick={() => setActiveSection(s)}
          >
            {s}
          </Button>
        ))}
      </div>

      {/* Active section content */}
      {activeSection === 'Suppliers & Pricing' && (
        <SuppliersSection detail={detail} />
      )}
      {activeSection === 'Gradations' && (
        <GradationsSection detail={detail} />
      )}
      {activeSection === 'Notes & Admin' && (
        <NotesAdminSection
          material={detail.material}
          onSaved={(patch) => {
            setDetail((prev) => prev ? { ...prev, material: { ...prev.material, ...patch } } : prev)
            if (onMaterialPatched) onMaterialPatched(materialId, patch)
          }}
          onOpenAddMaterial={onOpenAddMaterial}
          onOpenAddSupplier={() => setAddSupplierOpen(true)}
          onMerged={onMaterialMerged}
        />
      )}

      {/* Add Supplier Offering dialog */}
      <AddSupplierOfferingDialog
        open={addSupplierOpen}
        onClose={() => setAddSupplierOpen(false)}
        materialProfileId={materialId}
        existingSuppliers={detail ? detail.suppliers : []}
        onCreated={() => {
          setAddSupplierOpen(false)
          fetchDetail()
        }}
      />
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MaterialsTab () {
  const classes = useStyles()

  const [materials, setMaterials] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterColor, setFilterColor] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [search, setSearch] = useState('')

  // Only one row expanded at a time
  const [expandedId, setExpandedId] = useState(null)

  // Add Material dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  // ── Fetch ─────────────────────────────────────────────────────────────────────
  const fetchMaterials = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (filterColor) params.colorFamily = filterColor
      if (filterCategory) params.materialCategory = filterCategory
      if (search.trim()) params.search = search.trim()
      const res = await axios.get('/api/aggregate-dashboard/materials', { params })
      setMaterials(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      console.error('[MaterialsTab] fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [filterColor, filterCategory, search])

  const handleMaterialCreated = useCallback(async (materialId, isNew) => {
    await fetchMaterials()
    setExpandedId(materialId)
  }, [fetchMaterials])

  useEffect(() => {
    fetchMaterials()
  }, [fetchMaterials])

  // ── Row expand toggle (one at a time) ─────────────────────────────────────────
  const handleToggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  // ── Patch handler: update local list when material is saved from detail panel ──
  const handleMaterialPatched = (id, patch) => {
    setMaterials((prev) =>
      prev.map((m) => m._id === id ? { ...m, ...patch } : m)
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────────
  return (
    <div className={classes.root}>
      {/* Tab header */}
      <div className={classes.tabHeader}>
        <Typography variant="h6">
          Material Profiles
          {!loading && (
            <Typography component="span" variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
              ({materials.length})
            </Typography>
          )}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Material
        </Button>
      </div>

      {/* Filters */}
      <div className={classes.filters}>
        <TextField
          select
          size="small"
          label="Color Family"
          value={filterColor}
          onChange={(e) => { setFilterColor(e.target.value); setExpandedId(null) }}
          variant="outlined"
          style={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          {COLOR_FAMILIES.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Category"
          value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setExpandedId(null) }}
          variant="outlined"
          style={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setExpandedId(null) }}
          variant="outlined"
          style={{ minWidth: 200 }}
          placeholder="Name, description..."
        />
      </div>

      {/* Add Material dialog */}
      <AddMaterialDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onCreated={handleMaterialCreated}
      />

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" pt={6}>
          <CircularProgress size={32} />
        </Box>
      ) : materials.length === 0 ? (
        <Paper variant="outlined">
          <div className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary">
              No materials found. Sync a vendor to populate materials.
            </Typography>
          </div>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell} style={{ width: 32 }} />
                <TableCell className={classes.headerCell} style={{ width: 52 }}>Photo</TableCell>
                <TableCell className={classes.headerCell}>Display Name</TableCell>
                <TableCell className={classes.headerCell}>Color Family</TableCell>
                <TableCell className={classes.headerCell}>Category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materials.map((m) => {
                const isExpanded = expandedId === m._id
                const swatchColor = COLOR_SWATCH[m.colorFamily] || COLOR_SWATCH.UNKNOWN
                const chipTextColor = COLOR_CHIP_TEXT[m.colorFamily] || '#333'

                return (
                  <React.Fragment key={m._id}>
                    {/* Main row */}
                    <TableRow
                      hover
                      onClick={() => handleToggleExpand(m._id)}
                      style={{ cursor: 'pointer' }}
                      className={isExpanded ? classes.expandedHeaderRow : undefined}
                    >
                      <TableCell className={classes.cellCompact} style={{ width: 32, paddingLeft: 8 }}>
                        {isExpanded
                          ? <ExpandLessIcon fontSize="small" className={classes.expandArrow} />
                          : <ExpandMoreIcon fontSize="small" className={classes.expandArrow} />}
                      </TableCell>

                      {/* Photo or color swatch */}
                      <TableCell className={classes.cellCompact} style={{ width: 52, paddingTop: 6, paddingBottom: 6 }}>
                        {m.primaryImageUrl ? (
                          <img src={m.primaryImageUrl} alt={m.displayName} className={classes.thumbnail} />
                        ) : (
                          <div
                            className={classes.swatch}
                            style={{ backgroundColor: swatchColor }}
                            title={m.colorFamily}
                          />
                        )}
                      </TableCell>

                      <TableCell className={classes.cellCompact}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>{m.displayName}</Typography>
                        {(m.sourceColorName || m.description) && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {m.sourceColorName || m.description}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell className={classes.cellCompact}>
                        <Chip
                          label={m.colorFamily || 'UNKNOWN'}
                          size="small"
                          style={{
                            backgroundColor: swatchColor,
                            color: chipTextColor,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                            border: m.colorFamily === 'WHITE' ? '1px solid #bdbdbd' : undefined
                          }}
                        />
                      </TableCell>

                      <TableCell className={classes.cellCompact}>
                        <Typography variant="caption" color="textSecondary">
                          {(m.materialCategory || 'UNKNOWN').replace(/_/g, ' ')}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Expansion row */}
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        style={{
                          padding: 0,
                          borderBottom: isExpanded ? undefined : 'none'
                        }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <DetailPanel
                            materialId={m._id}
                            onMaterialPatched={handleMaterialPatched}
                            onOpenAddMaterial={() => setAddDialogOpen(true)}
                            onMaterialMerged={() => {
                              setExpandedId(null)
                              fetchMaterials()
                            }}
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  )
}
